import React, { useMemo } from "react";
import styled from "styled-components";
import * as d3 from "d3";
import { NVStackedAreaChartDownsampled } from "common/graphs/DownSampling";
import {
  tooltipContentGeneratorForUnits,
  formats,
} from "common/graphs/NVStackedAreaChart";
import { PERIOD_DASHBOARD_PERIOD_MAP } from "./utils";
import useDashboardData from "./useDashboardData";
import { atBqnTarget } from "common/api";
import Loading from "common/Loading";
import { PERIOD_TO_LINES } from "common/constants";
import { parseFlowsPerProtocolData } from "views/Statistics/Flows/api";

const Wrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const WrapperLoader = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 270px;
  color: var(--text-primary);
`;

const CHART_MARGIN = { top: 5, right: 10, bottom: 20, left: 60 };
const chartTooltipGenerator = tooltipContentGeneratorForUnits(
  "Flows",
  formats.noDecimals,
  true,
  false,
);

const getActiveFlowsData = ({ mode, bqnId, period, range }) => {
  const activePeriod = PERIOD_DASHBOARD_PERIOD_MAP[period];
  const { from, to } = range;
  return ifCl
    .run(
      atBqnTarget(
        `show statistics flow lines ${PERIOD_TO_LINES[activePeriod]}`,
        mode,
        bqnId,
      ),
    )
    .then((response) => ({
      data: parseFlowsPerProtocolData(response, activePeriod, from, to),
      range,
    }));
};

function ActiveFlows({ period, range }) {
  // The range of the dashboard is the clock of the chart, which is asked for
  // again with each new one of them.
  const { data: dataRange, isLoading } = useDashboardData({
    retrieve: ({ mode, bqnId, range }) =>
      getActiveFlowsData({ mode, bqnId, period, range }),
    onError: "Error fetching active flows data:",
    deps: [period],
    range,
  });

  // The fields are what the chart compares to decide whether it has to be
  // drawn again, so they are given the same identity while they are the same.
  const { data, range: rangeDrawn } = dataRange || {};
  const { items, fields } = data || {};
  const displayFields = useMemo(
    () => (fields ?? []).map((field) => ({ name: field, label: field })),
    [fields],
  );

  if (isLoading) {
    return (
      <WrapperLoader>
        <Loading />
      </WrapperLoader>
    );
  }

  if (!dataRange || !dataRange.data || dataRange.data.items.length === 0) {
    return (
      <WrapperLoader>
        No data available
      </WrapperLoader>
    );
  }

  return (
    <Wrapper>
      <NVStackedAreaChartDownsampled
        copyEndpoints={true}
        xAxisFormat={d3.timeFormat("%m/%d %H:%M")}
        xField="time"
        fields={displayFields}
        items={items}
        range={rangeDrawn}
        yAxisUnits={"Flows"}
        yAxisFormat={formats.noDecimals}
        tooltipContentGenerator={chartTooltipGenerator}
        yAxisTopGap={0.05}
        margin={CHART_MARGIN}
        xAxisTicksNumber={3}
      />
    </Wrapper>
  );
}

export default React.memo(ActiveFlows);
